// ═══════════════════════════════════════════════════════════
// نظام ERP - جمعية الدعوة بشمال الرياض
// App.jsx — V2: Top Navigation + سكرتير + تكليف مهمة + بدون إيموجي
// ═══════════════════════════════════════════════════════════

import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  LayoutDashboard, Globe2, Building2, Wallet, Heart, Users, Landmark, Megaphone, UserCircle, HandHeart,
  Shield, CalendarDays, LogOut, Menu, X, Bell, User, Briefcase, Send, ClipboardList, ListChecks, Settings,
} from "lucide-react";
import { C, ROLE_NAMES, ROLE_PERMISSIONS, getGreeting, fmtHijri, fmtDate, getDailyQuote, SETTINGS_EDITORS } from "./constants";
import { getNotifications, markNotificationRead, getUsersDirectory, getMe, hasSession, logoutUser } from "./supabaseClient";
import { notifyError } from "./useSupaTable";
import { GlobalErrorToast, ErrorBoundary } from "./erp-shared";

import LoginPage from "./LoginPage";
import ChangePasswordPage from "./ChangePasswordPage";
// الموديولات تتحمّل عند فتحها بس — شاشة الدخول أخف بكثير (BUG-055)
const Dashboard = lazy(() => import("./erp-dashboard"));
const BoardModule = lazy(() => import("./erp-board-module"));
const CommunitiesModule = lazy(() => import("./erp-communities-module"));
const MosquesModule = lazy(() => import("./erp-mosques-module"));
const FinanceModule = lazy(() => import("./erp-finance-module"));
const DonorCRMModule = lazy(() => import("./erp-donor-crm-module"));
const HRModule = lazy(() => import("./erp-hr-module"));
const FundraisingModule = lazy(() => import("./erp-fundraising-module"));
const CommunicationsModule = lazy(() => import("./erp-communications-module"));
const WomenModule = lazy(() => import("./erp-women-module"));
const VolunteerModule = lazy(() => import("./erp-volunteer-module"));
const GovernanceModule = lazy(() => import("./erp-governance-module"));
const EventsModule = lazy(() => import("./erp-events-module"));
const UserManagementModule = lazy(() => import("./erp-users-module"));
const SettingsModule = lazy(() => import("./erp-settings-module"));
const ProfilePage = lazy(() => import("./ProfilePage"));
const TasksPanel = lazy(() => import("./TasksPanel"));

// ───── التنقل بالروابط (#/finance ...) — Refresh يحافظ على الصفحة وزر الرجوع يعمل (BUG-054) ─────
const VALID_PAGES = new Set(["dashboard", "profile", "board", "communities", "mosques", "women", "events", "finance",
  "donor_crm", "fundraising", "hr", "comms", "volunteer", "governance", "user_mgmt", "settings"]);
const pageFromHash = () => {
  const id = window.location.hash.replace(/^#\/?/, "");
  return VALID_PAGES.has(id) ? id : "dashboard";
};

const NAV_ITEMS = [
  { id: "dashboard", label: "لوحة التحكم", icon: LayoutDashboard },
  { id: "board", label: "مجلس الإدارة", icon: Briefcase, adminOnly: true },
  { id: "communities", label: "الجاليات", icon: Globe2 },
  { id: "mosques", label: "المساجد", icon: Building2 },
  { id: "women", label: "النسائية", icon: UserCircle },
  { id: "events", label: "الفعاليات", icon: CalendarDays },
  { id: "finance", label: "المالية", icon: Wallet },
  { id: "donor_crm", label: "المتبرعين", icon: Heart },
  { id: "fundraising", label: "الموارد", icon: Landmark },
  { id: "hr", label: "الإدارية", icon: Users },
  { id: "comms", label: "الاتصال", icon: Megaphone },
  { id: "volunteer", label: "التطوع", icon: HandHeart },
  { id: "governance", label: "الحوكمة", icon: Shield },
  { id: "user_mgmt", label: "المستخدمين", icon: Users, adminOnly: true },
  // الإعدادات (BUG-050): exec / finance / system بس — نفس policies migration 014
  { id: "settings", label: "الإعدادات", icon: Settings, roles: SETTINGS_EDITORS },
];

const MODULE_PERM_MAP = {
  dashboard: "dashboard", board: "governance", communities: "communities", mosques: "mosques",
  women: "women", events: "events", finance: "finance", donor_crm: "donors",
  fundraising: "fundraising", hr: "hr", comms: "comms", volunteer: "volunteer",
  governance: "governance", user_mgmt: "system",
};

// هل الدور يملك الوصول للموديول؟ — نفس القاعدة للقائمة ولعرض الموديول نفسه (BUG-007)
const canAccess = (role, id) => {
  if (!role) return false;
  if (id === "profile") return true;
  const item = NAV_ITEMS.find(i => i.id === id);
  if (!item) return false;
  // مجلس الإدارة وإدارة المستخدمين: المسؤول التنفيذي ومدير النظام فقط (BUG-019)
  if (item.adminOnly && role !== "exec" && role !== "system") return false;
  if (item.roles) return item.roles.includes(role);
  const permissions = ROLE_PERMISSIONS[role] || [];
  return permissions.includes(MODULE_PERM_MAP[id]) || role === "exec" || role === "system" || role === "secretary";
};

// ───── مكون نافذة تكليف المهمة ─────
const TaskAssignModal = ({ open, onClose, onSave }) => {
  const [task, setTask] = useState({ assignee: "", title: "", description: "", priority: "عادي", deadline: "" });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    getUsersDirectory().then(setUsers).catch(err => notifyError(err.message));
  }, [open]);
  if (!open) return null;

  const inputStyle = {
    width: "100%", padding: "10px 12px", border: `1.5px solid ${C.border}`,
    borderRadius: 10, fontSize: 13, fontFamily: "inherit", color: C.text,
    background: "#fff", outline: "none",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,20,35,0.45)",
      backdropFilter: "blur(4px)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 1000, padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: "100%", maxWidth: 520,
        boxShadow: "0 24px 64px rgba(0,20,35,0.2)",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "18px 22px", borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ClipboardList size={18} style={{ color: C.teal }} />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>تكليف مهمة</h3>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.textLight, padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "18px 22px" }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 6 }}>
              الموظف المكلّف <span style={{ color: C.danger }}>*</span>
            </label>
            <select value={task.assignee} onChange={(e) => setTask({ ...task, assignee: e.target.value })} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="">— اختر الموظف —</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 6 }}>
              عنوان المهمة <span style={{ color: C.danger }}>*</span>
            </label>
            <input type="text" value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })}
              placeholder="عنوان واضح ومختصر" style={inputStyle} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 6 }}>التفاصيل</label>
            <textarea value={task.description} onChange={(e) => setTask({ ...task, description: e.target.value })}
              rows={3} placeholder="وصف تفصيلي للمهمة المطلوبة..." style={{ ...inputStyle, resize: "vertical" }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 6 }}>الأولوية</label>
              <select value={task.priority} onChange={(e) => setTask({ ...task, priority: e.target.value })} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="عاجل">عاجل</option>
                <option value="عادي">عادي</option>
                <option value="منخفض">منخفض</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 6 }}>الموعد النهائي</label>
              <input type="date" value={task.deadline} onChange={(e) => setTask({ ...task, deadline: e.target.value })} style={inputStyle} />
            </div>
          </div>
        </div>

        <div style={{ padding: "14px 22px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-start", gap: 8 }}>
          <button disabled={saving} onClick={async () => {
            if (saving) return; // منع الإرسال المزدوج (BUG-043)
            setSaving(true);
            const ok = await onSave(task);
            setSaving(false);
            if (ok) setTask({ assignee: "", title: "", description: "", priority: "عادي", deadline: "" });
          }} style={{
            padding: "10px 24px", border: "none", borderRadius: 10, fontSize: 13,
            fontWeight: 700, fontFamily: "inherit", cursor: "pointer",
            color: C.primary, background: C.accent, display: "flex", alignItems: "center", gap: 6,
          }}>
            <Send size={14} /> {saving ? "جاري الإرسال..." : "إرسال التكليف"}
          </button>
          <button onClick={onClose} style={{
            padding: "10px 20px", border: `1px solid ${C.border}`, borderRadius: 10,
            fontSize: 13, fontFamily: "inherit", cursor: "pointer", color: C.textLight, background: "#fff",
          }}>إلغاء</button>
        </div>
      </div>
    </div>
  );
};

// ───── دور غير معروف أو غير مسجّل: لا يُعرض أي موديول (BUG-006) ─────
const NoAccess = ({ onLogout, text = "حسابك غير مرتبط بدور معروف في النظام. تواصل مع مدير النظام." }) => (
  <div style={{ maxWidth: 420, margin: "60px auto", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 28, textAlign: "center" }}>
    <Shield size={32} style={{ color: C.danger }} />
    <h3 style={{ color: C.primary, margin: "12px 0 6px" }}>لا توجد صلاحية</h3>
    <p style={{ fontSize: 13, color: C.textLight, margin: "0 0 18px" }}>{text}</p>
    {onLogout && <button onClick={onLogout} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 18px", border: "none", borderRadius: 8, background: C.primary, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
      <LogOut size={14} /> تسجيل الخروج
    </button>}
  </div>
);

// ═══════════════════════════════════════════════════════════
// Top Navigation — بدون إيموجي + زر تكليف مهمة بدل العنوان
// ═══════════════════════════════════════════════════════════
const TopNav = ({ activeModule, setActiveModule, role, onLogout, mobileOpen, setMobileOpen, notifications = [], onMarkRead, onTaskAssign, onShowTasks }) => {
  const [showNotif, setShowNotif] = useState(false);
  const unread = notifications.filter(n => !n.is_read).length;
  const visibleItems = NAV_ITEMS.filter(i => canAccess(role, i.id));
  const canAssignTasks = ["exec", "secretary", "system"].includes(role) ||
    ["communities", "mosques", "finance", "fundraising", "comms", "women", "volunteer"].includes(role);

  return (
    <header style={{ background: "linear-gradient(135deg, #003d58 0%, #001a28 100%)", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 12px rgba(0,20,35,0.15)" }}>
      {/* الصف العلوي */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 20px", borderBottom: "1px solid rgba(255,255,255,0.1)",
      }}>
        {/* زر تكليف المهمة — في أقصى اليمين (RTL) مكان اللوجو */}
        <div style={{ display: "flex", alignItems: "center" }}>
          {canAssignTasks ? (
            <button onClick={onTaskAssign}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 4px 14px ${C.accent}55`; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 2px 8px ${C.accent}33`; }}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "9px 18px",
                background: `linear-gradient(135deg, ${C.accent} 0%, #c4a830 100%)`,
                borderRadius: 10,
                fontSize: 13, color: C.primary,
                fontWeight: 800, border: "none",
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: `0 2px 8px ${C.accent}33`,
                transition: "all 0.2s ease",
              }}>
              <ClipboardList size={16} />
              تكليف مهمة
            </button>
          ) : <div style={{ width: 1 }} />}
        </div>

        {/* التاريخ والتحية + الاقتباس اليومي */}
        <div style={{ textAlign: "center", maxWidth: 400 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
            {getGreeting()} &nbsp;|&nbsp; {fmtHijri(new Date())}
          </div>
          <div style={{ fontSize: 9, color: C.accent, marginTop: 2, fontStyle: "italic", opacity: 0.8 }}>
            {(() => { const q = getDailyQuote(); return `${q.text} — ${q.source}`; })()}
          </div>
        </div>

        {/* أزرار التحكم */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* اسم المستخدم */}
          <button onClick={() => setActiveModule("profile")} style={{
            display: "flex", alignItems: "center", gap: 5, padding: "5px 10px",
            background: `${C.accent}20`, borderRadius: 6, fontSize: 10, color: C.accent,
            fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit",
          }}>
            <User size={12} />
            {ROLE_NAMES[role]}
          </button>

          {/* مهامي — المكلَّف بها والتي كلّفت بها (BUG-072) */}
          <button onClick={onShowTasks} title="مهامي" style={{
            background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.7)", padding: 4,
          }}>
            <ListChecks size={15} />
          </button>

          {/* الإشعارات */}
          <div style={{ position: "relative" }}>
            <button onClick={() => setShowNotif(!showNotif)} style={{
              position: "relative", background: "none", border: "none", cursor: "pointer",
              color: "rgba(255,255,255,0.7)", padding: 4,
            }}>
              <Bell size={15} />
              {unread > 0 && <span style={{
                position: "absolute", top: -2, insetInlineStart: -2, width: 16, height: 16,
                background: C.danger, borderRadius: "50%", fontSize: 9, fontWeight: 700,
                color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
              }}>{unread}</span>}
            </button>
            {showNotif && (
              <>
                <div onClick={() => setShowNotif(false)} style={{ position: "fixed", inset: 0, zIndex: 98 }} />
                <div style={{
                  position: "absolute", top: "100%", insetInlineEnd: 0, marginTop: 8, width: 320,
                  background: "#fff", borderRadius: 14, boxShadow: "0 12px 36px rgba(0,20,35,0.15)",
                  zIndex: 99, overflow: "hidden",
                }}>
                  <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 6 }}>
                      <Bell size={14} style={{ color: C.teal }} /> الإشعارات
                    </span>
                    {unread > 0 && <button onClick={() => { notifications.forEach(n => !n.is_read && onMarkRead?.(n.id)); setShowNotif(false); }}
                      style={{ fontSize: 10, color: C.primary, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                      تعيين الكل كمقروء
                    </button>}
                  </div>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: C.textLight }}>لا توجد إشعارات</div>
                    ) : notifications.map(n => (
                      <div key={n.id} onClick={() => { onMarkRead?.(n.id); }}
                        style={{
                          padding: "10px 16px", borderBottom: `1px solid ${C.border}`, cursor: "pointer",
                          background: n.is_read ? "transparent" : `${C.primary}04`,
                        }}>
                        <div style={{ fontSize: 12, color: C.text, fontWeight: n.is_read ? 400 : 600 }}>{n.title}</div>
                        <div style={{ fontSize: 9, color: C.textLight, marginTop: 3 }}>
                          {n.created_at ? fmtDate(n.created_at) : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <button onClick={onLogout} title="خروج" style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", padding: 4 }}>
            <LogOut size={15} />
          </button>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mobile-menu-btn"
            style={{ background: "none", border: "none", cursor: "pointer", color: C.white, padding: 4, display: "none" }}>
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* التنقل الأفقي */}
      <nav style={{ display: "flex", overflowX: "auto", gap: 0, padding: "0 12px", scrollbarWidth: "none" }}>
        {visibleItems.map((item) => {
          const active = activeModule === item.id;
          const Icon = item.icon;
          return (
            <button key={item.id} onClick={() => { setActiveModule(item.id); setMobileOpen(false); }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
              style={{
              display: "flex", alignItems: "center", gap: 5, padding: "10px 14px", whiteSpace: "nowrap",
              background: "transparent",
              borderBottom: active ? `3px solid ${C.accent}` : "3px solid transparent",
              borderTop: "none", borderLeft: "none", borderRight: "none",
              color: active ? C.accent : "rgba(255,255,255,0.75)",
              cursor: "pointer", fontSize: 11, fontFamily: "inherit", fontWeight: active ? 700 : 400,
              transition: "all 0.2s",
            }}>
              <Icon size={14} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{
          position: "absolute", top: "100%", right: 0, left: 0, background: C.primary,
          padding: 12, zIndex: 100, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4,
          boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
        }}>
          {visibleItems.map((item) => {
            const active = activeModule === item.id;
            const Icon = item.icon;
            return (
              <button key={item.id} onClick={() => { setActiveModule(item.id); setMobileOpen(false); }} style={{
                display: "flex", alignItems: "center", gap: 6, padding: "10px 12px", borderRadius: 8,
                background: active ? `${C.accent}20` : "transparent",
                border: active ? `1px solid ${C.accent}40` : "1px solid transparent",
                color: active ? C.accent : "rgba(255,255,255,0.8)",
                cursor: "pointer", fontSize: 11, fontFamily: "inherit", fontWeight: active ? 700 : 400,
              }}>
                <Icon size={14} /><span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </header>
  );
};

// ═══════════════════════════════════════════════════════════
export default function App() {
  const [activeModule, setActiveModuleState] = useState(pageFromHash);
  const setActiveModule = (id) => {
    setActiveModuleState(id);
    if (pageFromHash() !== id) window.location.hash = `/${id}`;
  };
  useEffect(() => {
    const onHash = () => setActiveModuleState(pageFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  // الدخول = جلسة Supabase Auth صالحة (وليس قيمة في localStorage — BUG-005)
  const [isLoggedIn, setIsLoggedIn] = useState(() => hasSession());
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cnr_user")) || null; } catch { return null; }
  });
  // الدور من المستخدم المسجّل دائماً (حتى بعد Refresh) — دور غير معروف = بدون أي صلاحية (BUG-006)
  const role = ROLE_NAMES[currentUser?.role] ? currentUser.role : null;

  const handleLogin = (u) => { setCurrentUser(u); setIsLoggedIn(true); localStorage.setItem("cnr_user", JSON.stringify(u)); };
  const handleLogout = () => { logoutUser(); setIsLoggedIn(false); setCurrentUser(null); localStorage.removeItem("cnr_user"); localStorage.removeItem("cnr_token"); };
  // انتهاء الجلسة أو تعطيل الحساب أثناء الاستخدام → شاشة الدخول (BUG-015)
  useEffect(() => {
    const onExpired = () => { setIsLoggedIn(false); setCurrentUser(null); localStorage.removeItem("cnr_user"); };
    window.addEventListener("erp:session-expired", onExpired);
    return () => window.removeEventListener("erp:session-expired", onExpired);
  }, []);
  const handlePasswordChanged = () => { setCurrentUser({ ...currentUser, must_change_password: false }); localStorage.setItem("cnr_user", JSON.stringify({ ...currentUser, must_change_password: false })); };

  // الإشعارات والتكليفات تتحدّث كل دقيقة وعند الرجوع للنافذة (BUG-072)
  useEffect(() => {
    if (!currentUser?.id) return;
    const load = () => getNotifications(currentUser.id).then(setNotifications).catch(() => {});
    load();
    const timer = setInterval(load, 60 * 1000);
    window.addEventListener("focus", load);
    return () => { clearInterval(timer); window.removeEventListener("focus", load); };
  }, [currentUser?.id]);

  // التحقق من الجلسة مقابل الداتابيز عند الفتح وكل 5 دقائق وعند الرجوع للنافذة (BUG-005/015):
  // حساب غير موجود أو معطّل = خروج، والدور/البيانات تُحدَّث من الداتابيز وليس من localStorage
  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    const verify = async () => {
      let stored = null;
      try { stored = JSON.parse(localStorage.getItem("cnr_user")); } catch { /* */ }
      let fresh;
      try { fresh = await getMe(); } catch { return; } // انقطاع الشبكة لا يُخرج المستخدم
      if (cancelled) return;
      if (!fresh || !fresh.is_active) { handleLogout(); return; }
      const next = { ...stored, ...fresh, must_change_password: !!fresh.must_change_password };
      delete next.is_active;
      if (JSON.stringify(next) !== JSON.stringify(stored)) {
        setCurrentUser(next);
        localStorage.setItem("cnr_user", JSON.stringify(next));
      }
    };
    verify();
    const timer = setInterval(verify, 5 * 60 * 1000);
    window.addEventListener("focus", verify);
    return () => { cancelled = true; clearInterval(timer); window.removeEventListener("focus", verify); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, currentUser?.id]);

  if (!isLoggedIn) return <LoginPage onLogin={handleLogin} />;
  if (currentUser?.must_change_password) return <ChangePasswordPage user={currentUser} onComplete={handlePasswordChanged} />;

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(notifications.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (err) { notifyError(err.message); }
  };

  const handleTaskSave = async (task) => {
    try {
      const { supaInsert } = await import("./supabaseClient");
      if (!task.assignee || !task.title.trim()) { notifyError("الرجاء اختيار الموظف وإدخال عنوان المهمة"); return false; }
      // المهمة نفسها بحالة ومتابعة (مهامي) + إشعار للمكلَّف (BUG-072)
      await supaInsert("tasks", {
        assigned_to: Number(task.assignee), title: task.title.trim(), description: task.description || null,
        priority: task.priority, deadline: task.deadline || null,
      }, { minimal: true });
      await supaInsert("notifications", {
        user_id: Number(task.assignee),
        title: `تكليف جديد: ${task.title}`,
        body: `${task.description}\nالأولوية: ${task.priority}${task.deadline ? `\nالموعد: ${task.deadline}` : ""}`,
        type: "warning",
      }, { minimal: true });
      setShowTaskModal(false);
      return true;
    } catch (err) {
      notifyError(err.message);
      return false;
    }
  };

  const renderModule = () => {
    if (!role && activeModule !== "profile") return <NoAccess onLogout={handleLogout} />;
    if (!canAccess(role, activeModule) && activeModule !== "dashboard") return <NoAccess text="لا تملك صلاحية الوصول لهذه الصفحة." />;
    const userName = currentUser?.name || ROLE_NAMES[role];
    switch (activeModule) {
      case "dashboard": return <Dashboard currentRole={role} userName={userName} userId={currentUser?.id} />;
      case "board": return <BoardModule role={role} />;
      case "communities": return <CommunitiesModule />;
      case "mosques": return <MosquesModule />;
      case "finance": return <FinanceModule />;
      case "donor_crm": return <DonorCRMModule />;
      case "hr": return <HRModule />;
      case "fundraising": return <FundraisingModule />;
      case "comms": return <CommunicationsModule />;
      case "women": return <WomenModule />;
      case "volunteer": return <VolunteerModule />;
      case "governance": return <GovernanceModule />;
      case "events": return <EventsModule />;
      case "user_mgmt": return <UserManagementModule />;
      case "settings": return <SettingsModule />;
      case "profile": return <ProfilePage user={currentUser} onLogout={handleLogout} onUserUpdate={(u) => { setCurrentUser(u); localStorage.setItem("cnr_user", JSON.stringify(u)); }} />;
      default: return <Dashboard currentRole={role} userName={userName} userId={currentUser?.id} />;
    }
  };

  return (
    <div style={{ direction: "rtl", fontFamily: "'Readex Pro', 'IBM Plex Sans Arabic', sans-serif", minHeight: "100vh", background: C.bg }}>
      <TopNav activeModule={activeModule} setActiveModule={setActiveModule} role={role}
        onLogout={handleLogout} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}
        notifications={notifications} onMarkRead={handleMarkRead}
        onTaskAssign={() => setShowTaskModal(true)} onShowTasks={() => setShowTasks(true)} />
      <main style={{ padding: 20, maxWidth: 1400, margin: "0 auto" }}>
        <ErrorBoundary key={activeModule}>
          <Suspense fallback={<div style={{ padding: 40, textAlign: "center", fontSize: 13, color: C.textLight }}>جاري التحميل...</div>}>
            {renderModule()}
          </Suspense>
        </ErrorBoundary>
      </main>

      {/* نافذة تكليف المهمة */}
      <TaskAssignModal open={showTaskModal} onClose={() => setShowTaskModal(false)} onSave={handleTaskSave} />
      {showTasks && <Suspense fallback={null}><TasksPanel open onClose={() => setShowTasks(false)} userId={currentUser?.id} /></Suspense>}
      <GlobalErrorToast />

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Readex Pro', 'IBM Plex Sans Arabic', sans-serif; background: ${C.bg}; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
        nav::-webkit-scrollbar { display: none; }
        button { transition: all 0.2s ease; }
        button:active:not(:disabled) { transform: scale(0.98); }
        input, select, textarea { transition: border-color 0.2s ease, box-shadow 0.2s ease; }
        input:focus, select:focus, textarea:focus { border-color: ${C.accent} !important; box-shadow: 0 0 0 3px ${C.accent}25 !important; }
        @media (max-width: 768px) { .mobile-menu-btn { display: block !important; } nav { display: none !important; } }
      `}</style>
    </div>
  );
}
